/**
 * Tiny WebAudio-synthesized sound effects.
 *
 * Used for sensorial tap feedback that respects the "Tactile Storybook" North
 * Star — soft paper/wooden taps, not arcade beeps. Synthesized so we don't
 * ship sound files (offline-first principle: no extra payload, no precache).
 *
 * Calls are no-ops on:
 *  - SSR (no AudioContext)
 *  - prefers-reduced-motion (treated as a sensory-reduction signal too)
 *  - First load before any user gesture (browsers block AudioContext until then)
 */

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (typeof AudioContext === 'undefined' && typeof (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext === 'undefined') {
    return null;
  }
  if (!ctx) {
    try {
      const Ctor = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
        ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  // iOS sometimes leaves the context suspended after a navigation; resume best-effort.
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => { /* ignore */ });
  }
  return ctx;
}

function reduceMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

export type SfxKind =
  | 'tap'
  | 'pop'
  | 'page'
  | 'comic-boing'
  | 'comic-pow'
  | 'comic-magic'
  | 'comic-clap'
  | 'shake-clear';

/**
 * Play a short synthesized sound effect.
 *
 * - tap:          soft wooden block — used for tile/button taps
 * - pop:          high-pitched bubble — used for match/success microevents
 * - page:         low whoosh — used for view transitions / nav (subtle)
 * - comic-boing:  elastic spring wobble — used for comic bouncy actions
 * - comic-pow:    cartoon punch impact — used for comic action stickers
 * - comic-magic:  sparkle chime triad — used for magical sticker moments
 * - comic-clap:   double snap / applause clap
 */
export function playSfx(kind: SfxKind = 'tap'): void {
  if (reduceMotion()) return;
  const c = getCtx();
  if (!c) return;
  const now = c.currentTime;

  const osc = c.createOscillator();
  const gain = c.createGain();
  const filter = c.createBiquadFilter();

  filter.type = 'lowpass';
  osc.connect(filter);
  filter.connect(gain);
  gain.connect(c.destination);

  switch (kind) {
    case 'tap': {
      // Soft wooden block — short percussive thunk around 320Hz
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(420, now);
      osc.frequency.exponentialRampToValueAtTime(180, now + 0.08);
      filter.frequency.setValueAtTime(1200, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.10, now + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
      osc.start(now);
      osc.stop(now + 0.13);
      break;
    }
    case 'pop': {
      // Bubbly high pop — used for match microevents
      osc.type = 'sine';
      osc.frequency.setValueAtTime(680, now);
      osc.frequency.exponentialRampToValueAtTime(1200, now + 0.06);
      filter.frequency.setValueAtTime(2400, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.08, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
      osc.start(now);
      osc.stop(now + 0.18);
      break;
    }
    case 'page': {
      // Low papery whoosh — used for view transitions
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(160, now);
      osc.frequency.exponentialRampToValueAtTime(80, now + 0.18);
      filter.frequency.setValueAtTime(600, now);
      filter.frequency.exponentialRampToValueAtTime(200, now + 0.18);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.06, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
      osc.start(now);
      osc.stop(now + 0.24);
      break;
    }
    case 'comic-boing': {
      // Elastic spring boing: upward sweep with pitch wobble
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(160, now);
      osc.frequency.exponentialRampToValueAtTime(540, now + 0.12);
      osc.frequency.exponentialRampToValueAtTime(320, now + 0.22);
      osc.frequency.exponentialRampToValueAtTime(440, now + 0.28);
      filter.frequency.setValueAtTime(1800, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.14, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
      osc.start(now);
      osc.stop(now + 0.34);
      break;
    }
    case 'comic-pow': {
      // Punchy cartoon thud
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(380, now);
      osc.frequency.exponentialRampToValueAtTime(55, now + 0.14);
      filter.frequency.setValueAtTime(900, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.18, now + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.20);
      osc.start(now);
      osc.stop(now + 0.22);
      break;
    }
    case 'comic-magic': {
      // Playful twinkle triad (C5 -> E5 -> G5)
      osc.type = 'sine';
      osc.frequency.setValueAtTime(523, now);
      osc.frequency.setValueAtTime(659, now + 0.08);
      osc.frequency.setValueAtTime(784, now + 0.16);
      filter.frequency.setValueAtTime(3000, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.01);
      gain.gain.setValueAtTime(0.12, now + 0.18);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.36);
      osc.start(now);
      osc.stop(now + 0.38);
      break;
    }
    case 'comic-clap': {
      // Crisp snap clap
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(800, now);
      osc.frequency.exponentialRampToValueAtTime(220, now + 0.04);
      osc.frequency.setValueAtTime(750, now + 0.06);
      osc.frequency.exponentialRampToValueAtTime(200, now + 0.12);
      filter.frequency.setValueAtTime(2200, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.004);
      gain.gain.setValueAtTime(0.10, now + 0.064);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
      osc.start(now);
      osc.stop(now + 0.20);
      break;
    }
    case 'shake-clear': {
      // Magic erase / rattle whoosh: simulated Etch-a-Sketch rattle + clean sweep chime
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(320, now);
      osc.frequency.exponentialRampToValueAtTime(840, now + 0.12);
      osc.frequency.exponentialRampToValueAtTime(220, now + 0.28);
      filter.frequency.setValueAtTime(3200, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.18, now + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.04, now + 0.13);
      gain.gain.exponentialRampToValueAtTime(0.16, now + 0.20);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
      osc.start(now);
      osc.stop(now + 0.33);
      break;
    }
  }
}
